import type { PanelInfo } from './PanelCollector'

/**
 * Draws panel type labels onto a screenshot so Claude can visually
 * identify which region is which panel. Labels are drawn in the
 * top-left corner of each panel's region.
 *
 * This only modifies the image sent to Claude — not the actual UI.
 */
export async function annotateScreenshot(
  base64Jpeg: string,
  panels: PanelInfo[],
  captureWidth: number,
  captureHeight: number,
): Promise<string> {
  if (panels.length === 0) return base64Jpeg

  const vw = window.innerWidth
  const vh = window.innerHeight

  // Load the screenshot into a canvas
  const img = await loadImage(`data:image/jpeg;base64,${base64Jpeg}`)
  const canvas = document.createElement('canvas')
  canvas.width = captureWidth
  canvas.height = captureHeight
  const ctx = canvas.getContext('2d')!
  ctx.drawImage(img, 0, 0, captureWidth, captureHeight)

  // Draw a label in each panel's region
  for (const panel of panels) {
    const sx = Math.round((panel.bounds.x / vw) * captureWidth)
    const sy = Math.round((panel.bounds.y / vh) * captureHeight)

    const label = panel.panelType.toUpperCase()

    // Label style: small semi-transparent background with white text
    ctx.font = 'bold 11px system-ui, sans-serif'
    const metrics = ctx.measureText(label)
    const padX = 5
    const padY = 3
    const labelW = metrics.width + padX * 2
    const labelH = 14 + padY * 2

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.7)'
    ctx.fillRect(sx + 2, sy + 2, labelW, labelH)

    // Text
    ctx.fillStyle = '#ffffff'
    ctx.fillText(label, sx + 2 + padX, sy + 2 + padY + 11)
  }

  // Export as JPEG base64
  const annotated = canvas.toDataURL('image/jpeg', 0.8).split(',')[1]
  return annotated
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}
